/**
 * Darstellung von Messwerten.
 *
 * Eine Regel zieht sich durch: Im Logbuch zaehlt die **Ortszeit**. Gespeichert
 * ist `start_utc` plus `tz_offset_min`; bei Diving-Log-Tauchgaengen fehlt der
 * Offset, dort steht in `start_utc` bereits die Ortszeit. Wer das verwechselt,
 * verschiebt jeden Garmin-Tauchgang um Stunden.
 */

const MONATE = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun',
  'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

/** Ortszeit als Date-Objekt in UTC-Feldern — nur zur Anzeige, nie zum Rechnen. */
export function ortszeit(tg) {
  if (!tg?.start_utc) return null;
  const roh = String(tg.start_utc);
  const zeit = new Date(roh);
  if (Number.isNaN(zeit.getTime())) return null;
  const versatz = tg.tz_offset_min;
  if (versatz === null || versatz === undefined || !/[+-]\d{2}:?\d{2}$|Z$/.test(roh)) {
    // Ohne Offset ist der Zeitstempel bereits Ortszeit — nur die Zeitzone des
    // Browsers darf ihn nicht noch einmal verschieben.
    return new Date(`${roh.replace(/[Zz]$/, '')}Z`);
  }
  return new Date(zeit.getTime() + versatz * 60000);
}

export function datum(tg) {
  const zeit = ortszeit(tg);
  if (!zeit) return '—';
  return `${String(zeit.getUTCDate()).padStart(2, '0')}. ${MONATE[zeit.getUTCMonth()]} `
    + `${zeit.getUTCFullYear()}`;
}

export function uhrzeit(tg) {
  const zeit = ortszeit(tg);
  if (!zeit) return '';
  return `${String(zeit.getUTCHours()).padStart(2, '0')}:`
    + `${String(zeit.getUTCMinutes()).padStart(2, '0')}`;
}

/**
 * „Mär 2019" aus einem ISO-Datum.
 *
 * Anders als `datum()` nimmt das keinen Tauchgang, sondern eine Zeichenkette —
 * es beschreibt einen Zeitraum (eine Reise, eine Buddy-Bekanntschaft), und ein
 * Zeitraum hat keine Zeitzone.
 */
export function monatJahr(iso) {
  const treffer = /^(\d{4})-(\d{2})/.exec(String(iso ?? ''));
  if (!treffer) return '';
  return `${MONATE[Number(treffer[2]) - 1]} ${treffer[1]}`;
}

export function jahr(tg) {
  const zeit = ortszeit(tg);
  return zeit ? zeit.getUTCFullYear() : null;
}

export function tiefe(meter) {
  return typeof meter === 'number' ? `${meter.toFixed(1)} m` : '—';
}

export function dauer(sekunden) {
  if (typeof sekunden !== 'number') return '—';
  const minuten = Math.round(sekunden / 60);
  if (minuten < 60) return `${minuten} min`;
  return `${Math.floor(minuten / 60)} h ${String(minuten % 60).padStart(2, '0')} min`;
}

export function temperatur(grad) {
  return typeof grad === 'number' ? `${grad.toFixed(1)} °C` : '—';
}

export function pause(sekunden) {
  if (typeof sekunden !== 'number') return '—';
  const stunden = sekunden / 3600;
  if (stunden < 48) return `${stunden.toFixed(1)} h`;
  return `${Math.round(stunden / 24)} Tage`;
}

export function zahl(wert, einheit = '', stellen = 0) {
  if (typeof wert !== 'number') return '—';
  return `${wert.toFixed(stellen)}${einheit ? ` ${einheit}` : ''}`;
}

/**
 * Auf hoechstens `stellen` Nachkommastellen — ohne nachlaufende Nullen.
 *
 * `zahl()` schreibt immer genau so viele Stellen, wie verlangt: richtig in
 * einer Tabelle, falsch bei einer Zahl, die aus einer Rechnung kommt. 200 bar
 * minus 44.8 bar sind 155.2 und nicht 155.20 — und ungerundet ueberhaupt
 * 155.20000000000002, weil Zehntel binaer nicht aufgehen.
 */
export function gerundet(wert, einheit = '', stellen = 2) {
  if (typeof wert !== 'number' || !Number.isFinite(wert)) return '—';
  return `${Number(wert.toFixed(stellen))}${einheit ? ` ${einheit}` : ''}`;
}

export function koordinaten(lat, lon) {
  if (typeof lat !== 'number' || typeof lon !== 'number') return null;
  const richtung = (wert, positiv, negativ) => `${Math.abs(wert).toFixed(4)}° `
    + `${wert >= 0 ? positiv : negativ}`;
  return `${richtung(lat, 'N', 'S')}, ${richtung(lon, 'E', 'W')}`;
}

/** "salt" -> "Salzwasser". Unbekanntes bleibt stehen, statt zu verschwinden. */
const WOERTER = {
  salt: 'Salzwasser', fresh: 'Süsswasser', brackish: 'Brackwasser',
  boat: 'Boot', shore: 'Ufer', ice: 'Eis', cave: 'Höhle', pool: 'Becken',
  none: 'keine', light: 'leicht', moderate: 'mässig', strong: 'stark',
  flat: 'ruhig', small_waves: 'kleine Wellen', large_waves: 'grosse Wellen',
  choppy: 'kabbelig',
  excellent: 'sehr gut', good: 'gut', medium: 'mittel', poor: 'schlecht',
  wet: 'Nassanzug', dry: 'Trockenanzug', semidry: 'Halbtrocken', shorty: 'Shorty',
  skin: 'Haut', air: 'Luft', eanx: 'Nitrox', trimix: 'Trimix',
  aluminum: 'Aluminium', steel: 'Stahl',
  drift: 'Strömung', night: 'Nacht', wreck: 'Wrack', cave_dive: 'Höhle',
  deep: 'tief', photo: 'Fotografie', training: 'Ausbildung', fun: 'Fun',
  ice_dive: 'Eis', mountain_lake: 'Bergsee', river: 'Fluss', search: 'Suchen',
  recovery: 'Bergung', free_diving: 'Apnoe', rescue: 'Rettung', guide: 'Führung',
  exam: 'Prüfung', student: 'Schüler', sidemount: 'Sidemount',
};

export function wort(schluessel) {
  if (!schluessel) return null;
  return WOERTER[schluessel] ?? schluessel;
}

export function woerter(liste) {
  if (!liste) return null;
  return String(liste).split(',').map((t) => wort(t.trim())).filter(Boolean).join(' · ');
}

/**
 * Die ersten Werte eines Blocks als Kurzfassung — Werte, keine Beschriftungen.
 *
 * Steht auf der Detailseite in der Kopfzeile eines zugeklappten Blocks und
 * beantwortet die eine Frage, die man im Vorbeiblaettern hat: „steht hier
 * etwas, das mich jetzt interessiert?" Beschriftungen bleiben deshalb weg —
 * sie verdoppeln die Laenge und sagen im Vorbeigehen nichts.
 */
export function kurzfassung(werte, hoechstens = 3) {
  return werte
    .map((wert) => String(wert ?? '').replace(/\s+/g, ' ').trim())
    .filter((wert) => wert && wert !== '—')
    .slice(0, hoechstens)
    .join(' · ');
}
